import { SquarePen, Trash2 } from "lucide-react";
import { useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/app/components/ui/dialog";
import type { ReferenceItem } from "@/app/types/resume";

import { StaticValue } from "../../../seed/constants";
import { usePatchResumeSection } from "../../../services/create-resume/queries";
import { RESUME_ACTIONS, RESUME_SECTIONS } from "../../../services/create-resume/resumeSections";

interface ReferenceSummaryBoxProps {
	item: ReferenceItem;
}

const ReferenceSummaryBox = ({ item }: ReferenceSummaryBoxProps) => {
	const [cookies] = useCookies(["resume"]);
	const navigate = useNavigate();

	const refInfoHandler = () => {
		navigate("/resume/reference-information", {
			state: {
				referenceId: item.id,
			},
		});
	};

	function onSuccess() {
		navigate(0);
	}

	function onError() {
		//TODO: handle error
	}

	const staticValue = new StaticValue();
	const staticValues = staticValue.getDefaultValues();
	const resumeId = cookies[staticValues.localStorageResumeId];

	const patchSection = usePatchResumeSection();

	function deleteReference() {
		patchSection.mutate(
			{
				resumeId,
				section: RESUME_SECTIONS.REFERENCE,
				data: { referenceId: item.id },
				action: RESUME_ACTIONS.DELETE,
			} as never,
			{ onSuccess, onError },
		);
	}

	const [showDeleteWarning, setShowDeleteWarning] = useState(false);

	return (
		<>
			<div className="mb-5 flex rounded-lg border border-black/10 p-5">
				<div className="flex w-full flex-col gap-3 md:flex-row md:justify-between">
					<div>
						<p className="m-0 font-semibold text-base text-gray-900">{item.name}</p>
						<p>
							{item.designation}, {item.workPlace}
						</p>
						<p>{item.address}</p>
						<p>{item.phoneNumber}</p>
						<p> {item.email}</p>
					</div>
					<div className="flex items-start justify-end gap-2">
						<button
							type="button"
							onClick={refInfoHandler}
							className="flex min-w-max cursor-pointer items-center gap-1.5 rounded-lg border border-gray-100 px-2 py-2.5 text-sm leading-none"
						>
							<SquarePen className="size-4 text-[#7a7a7a]" /> Edit
						</button>
						<button
							type="button"
							onClick={() => setShowDeleteWarning(true)}
							className="flex cursor-pointer items-center rounded-lg border border-gray-100 px-2 py-2.5 leading-none"
						>
							<Trash2 className="size-4 text-red-600" />
							<span className="sr-only">Delete</span>
						</button>
					</div>
				</div>
			</div>
			<Dialog open={showDeleteWarning} onOpenChange={setShowDeleteWarning}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>You are about to delete this item</DialogTitle>
						<DialogDescription>Are you sure you want to delete this item?</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button type="button" variant="secondary" onClick={() => setShowDeleteWarning(false)}>
							Cancel
						</Button>
						<Button
							type="button"
							onClick={() => {
								deleteReference();
								setShowDeleteWarning(false);
							}}
						>
							Delete
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default ReferenceSummaryBox;
